<template>
  <div>
    <b-modal
      v-if="!!callCenterPopupContent"
      visible
      id="call-center-popup"
      centered
      hide-footer
      hide-header
      size="lg"
      @hide="onCloseCallCenterPopup()"
    >
      <div v-html="callCenterPopupContent"></div>
      <b-link block class="centered-block close-button" variant="secondary" @click="closeCallCenterPopup()">&times;</b-link>
    </b-modal>
    <div id="choices" class="flex" :class="{'content-confirmation': isConfirmationPopup}">
      <template v-if="!isSessionValid">
        <SessionComplete :session="session">You have been logged out due to inactivity.</SessionComplete>
      </template>
      <template v-else-if="session.is_background_session && session.is_hidden">
        <div><!-- empty background --></div>
      </template>
      <template v-else-if="needsCustomerMatch">
        <CustomerMatch
          :session="session"
          @back="goBack"
          @done="updateInputState(!isCustomerPortal)"
          @fail="onCustomerMatchFailed"
        ></CustomerMatch>
      </template>
      <template v-else-if="needsCustomerVerification">
        <CustomerVerify :session="session" @back="goBack" @done="updateInputState(!isCustomerPortal)"></CustomerVerify>
      </template>
      <template v-else-if="needsRMAItems">
        <SelectActionItems
          label="Submit"
          :defaults="inputData"
          :input="selectedInput"
          :session="session"
          @select="selectItemsForRMAOrCancel"
          @back="goBack"
        ></SelectActionItems>
      </template>
      <template v-else-if="needsRebillItems">
        <SelectActionItems
          rebill
          label="Submit"
          :defaults="inputData"
          :input="selectedInput"
          :session="session"
          @select="selectItemsForRMAOrCancel"
          @back="goBack"
        ></SelectActionItems>
      </template>
      <template v-else-if="needsVariants">
        <Variants
          :item="currentSelectedItem"
          :new-campaign-product="selectedCampaignProduct"
          :quantity="(inputData && inputData['quantity']) ? inputData['quantity'] : currentSelectedItem.quantity"
          :subscription-only="subscriptionOnly"
          :session="session"
          @select="selectVariants"
          @back="goBack"
        ></Variants>
      </template>
      <template v-else-if="isCustomerPortal">
        <CustomerPortal
          :input="selectedInput"
          :inputType="inputType"
          @submit="selectInput"
          @selectItemInput="selectItemInput"
        ></CustomerPortal>
      </template>
      <template v-else-if="isItemStatus()">
        <OrderItems
          :key="'session-' + selectedInput.id.toString()"
          :input="selectedInput"
          :items="currentSelectedItem ? [currentSelectedItem] : []"
          :session="session"
          @submit="selectInput"
          @back="goBack"
          @edit="editItem"
        >
          <SessionContent :session="session"></SessionContent>
        </OrderItems>
      </template>
      <template v-else-if="isCheckout">
        <Checkout :session="session" @submit="submitInputAndData" @back="goBack"></Checkout>
      </template>
      <template v-else-if="isInputActionStatus()">
        <InputActionStatus
          :input="selectedInput"
          :session="session"
          @done="submit"
          @back="goBack"
          @edit="editItem"
        ></InputActionStatus>
      </template>
      <template v-else-if="isCheckoutConfirm">
        <CheckoutConfirm :input="selectedInput" :session="session" @done="submit" @back="goBack"></CheckoutConfirm>
      </template>
      <template v-else-if="isSessionComplete">
        <SessionComplete :session="session"><div v-html="thankYouMessage"></div></SessionComplete>
      </template>
      <template v-else-if="isCampaignSelect">
        <SelectCampaign :input="selectedInput" :session="session" @select="submitInputData" @back="goBack"></SelectCampaign>
      </template>
      <template v-else-if="isSelectedProductRequired()">
        <SelectExchangeProduct
          :item="selectedItem"
          :subscription-only="subscriptionOnly"
          :quantity-type="quantityType"
          :session="session"
          @select="submitInputAndData"
          @back="goBack"
        ></SelectExchangeProduct>
      </template>
      <template v-else-if="isItemSelection">
        <div>
          <SessionContent :session="session"></SessionContent>
          <SelectItems
            label="Continue"
            item-select-label="Get Help"
            :defaults="selectedItems"
            :input="selectedInput"
            :session="session"
            @select="onItemsSelected"
            @selectItemInput="selectItemInput"
            @back="goBack"
          ></SelectItems>
        </div>
      </template>
      <template v-else-if="isOrderTracking">
        <OrderTracking
          :item="session.selected_item"
          :fulfillments="fulfillments"
          :session="session"
          show-inputs
          @done="selectInput"
          @clear="goBack"
        />
      </template>
      <template v-else>
        <div class="inputs" v-if="canDisplayTitle">
          <StepTitle :session="session">
            <div v-if="sessionTitle" v-html="sessionTitle" class="how-can label-content-width"
            :class="{'mb-5': !session.subtitle && isSurveyStepCategory}"></div>
            <template v-if="session && !session.enhanced_mode && session.subtitle" #subtitle><div v-html="sessionSubTitle" class="option"></div></template>
            <template v-if="session && session.content && session.content.length" #content>
              <div :class="{'page-content': session.enhanced_mode}" v-html="sessionContent"></div>
            </template>
            <template v-if="showSelectedProduct" #product>
              <div :class="{'col-md-8 col-lg-6 selected-product-col': session && !session.enhanced_mode}">
                <div class="selected-product-wrapper" :class="{'product-wrapper': session && !session.enhanced_mode,
              'product-wrapper-enhanced': session && session.enhanced_mode}">
                  <div class="orders-container" :class="{'orders-container-normal': isOrderContainerNormal}">
                    <div class="order-container">
                      <OrderItemCard
                        :item="currentSelectedItem"
                        :currency="session.currency || 'USD'"
                        :show-buttons="false"
                        :show-next-bill-info="false"
                        :show-quantity="true"
                        :show-variants="false"
                        :show-price-next-to-name="true"
                        :session="session"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <template #static-data>
              <StepMedia :session="session" />
            </template>
          </StepTitle>
          <template v-if="session && (!session.enhanced_mode || !session.content)">
            <div v-if="needsInputFields">
              <InputFields @back="goBack" :fields="selectedInput.input_fields" :item="selectedItem" :session="session" @submit="submitInputData"></InputFields>
            </div>
            <div v-else-if="isCustomerInfo">
              <CustomerInfo :input="selectedInput" :session="session" @submit="selectInput" @back="goBack"></CustomerInfo>
            </div>
            <div v-else-if="isPaymentUpdater">
              <PaymentUpdater :rebill="rebill" :input="selectedInput" :session="session" @submit="selectInput" @back="goBack"></PaymentUpdater>
            </div>
            <div v-else-if="isPaymentInfo">
              <PaymentInfo :input="selectedInput" :session="session" @submit="selectInput" @back="goBack"></PaymentInfo>
            </div>
            <div v-else-if="isRadioGroup || isSelect || isRadioOrSelect">
              <ChoiceFields @submit="selectInput" @back="goBack" :key="session ? session.step : 0" :session="session"></ChoiceFields>
            </div>
            <div v-else-if="isButtonGroup">
              <div v-if="isIconButtons" class="home-icons" :class="{'wrapper': isBoxButton}">
                <b-row class="justify-content-sm-center boxes icon-buttons">
                  <template v-if="isBoxButton">
                    <b-col cols="6" sm="12" v-for="input in session.valid_inputs" :key="'mob-choice-' + input.id" @click="selectInput(input)" class="choice d-block d-sm-none">
                      <div class="box" :class="getCustomClass(input)" v-lazy-container="{ selector: 'img' }">
                        <img :data-src="input.icon.file" alt="" block variant="primary"/>
                        <h2 v-html="input.formatted_label"></h2>
                      </div>
                    </b-col>
                  </template>
                  <b-col cols="12" sm="auto" v-for="input in session.valid_inputs" :key="'choice-' + input.id" @click="selectInput(input)" class="choice" :class="{'d-none d-sm-block': isBoxButton}">
                    <div class="box hide-on-phone" :class="getCustomClass(input)" v-lazy-container="{ selector: 'img' }">
                      <img :data-src="input.icon.file" alt="" block variant="primary"/>
                      <h2 v-html="input.formatted_label"></h2>
                    </div>
                    <b-button block
                              class="d-sm-none btn btn-default btn-session gutters mob-home-btn"
                              :class="getCustomClass(input)"
                              variant="primary">
                      <img :src="input.icon.file" alt="" block variant="primary" class="mob-home-btn-icon"/>
                      <p v-html="input.formatted_label" class="new-btns"></p>
                    </b-button>
                  </b-col>
                </b-row>
              </div>
              <div v-else>
                <b-row class="justify-content-md-center boxes claim">
                  <b-col :md="singleColumnButtons && session.is_legacy ? 8 : 9" :lg="singleColumnButtons && session.is_legacy ? 6 : 7" class="my-class">
                    <b-row class="choice-fields-btns">
                      <b-col v-for="input in session.valid_inputs" :key="'choice-' + input.id" :md="singleColumnButtons ? 12 : 6" class="text-center">
                        <InputButton :input="input" :session="session" @click="selectInput(input)"></InputButton>
                      </b-col>
                    </b-row>
                  </b-col>
                </b-row>
              </div>
              <StepBackButton @back="goBack" :single-column-buttons="singleColumnButtons" :session="session" />
            </div>
            <div v-else-if="session && !session.is_legacy">
              <b-row class="justify-content-md-center boxes welcome choice-fields-btns">
                <b-col v-for="input in session.valid_inputs" :key="'choice-' + input.id" :md="session.is_popup ? 5 : 4" :lg="session.is_popup ? 4 : 3" class="text-center">
                  <button @click="selectInput(input)"
                          class="btn-base input-choice-btn"
                          :class="getCustomClass(input, true)"
                          :style="getStyles(input)"
                  ><div v-html="sessionInputLabel(input)"></div>
                  </button>
                </b-col>
                <b-col v-for="input in (session.additional_inputs || [])" :key="'choice-' + input.id" :md="session.is_popup ? 5 : 4" :lg="session.is_popup ? 4 : 3" class="text-center">
                  <button @click="selectInput(input)"
                          class="btn-base input-choice-btn new-btn-outline-success"
                          :class="getCustomClass(input, true)"
                          :style="getStyles(input)"
                  ><div v-html="sessionInputLabel(input)"></div>
                  </button>
                </b-col>
              </b-row>
            </div>
            <div v-else>
              <b-row class="justify-content-md-center boxes welcome">
                <b-col v-if="showBackButton" md="4" lg="3">
                  <button type="button" @click="goBack()" class="btn-base input-choice-btn new-btn-outline-success stroke-2x">Back</button>
                </b-col>
                <b-col md="4" lg="3" class="mt-2 mt-md-0" v-if="showFinalSubmitButton">
                  <button type="button" @click="submitForm()" class="btn-base input-choice-btn stroke-2x">Submit</button>
                </b-col>
              </b-row>
            </div>
          </template>
          <template v-else>
            <b-row v-if="showBackButton" class="justify-content-md-center boxes welcome">
              <b-col md="4" lg="3">
                <button type="button" @click="goBack()" class="btn-base input-choice-btn new-btn-outline-success stroke-2x">Back</button>
              </b-col>
            </b-row>
          </template>
        </div>
      </template>
    </div>
    <b-link v-if="canShowCloseIcon" block class="centered-block close-button" @click="closePopup()" variant="secondary">&times;</b-link>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import {
  SessionStatus,
  InputType,
  InputState,
  VariantType,
  MobileIconButtonType,
  StepButtonType,
  CustomerPortalInputType,
  QuantityType,
  MFAStatus,
  PreviewType,
  SessionSource,
  PreMatchStatus,
  SurveyCategories
} from '@/constants'
import {
  getCustomInputClass,
  getItemImageUrl,
  getErrorData,
  isFirstInputDone,
  setFirstInputDone,
  updateFontSizes,
  recalculateFontSizes,
  getPlaceholderImageUrl,
  isItemSelectionInput,
  getTrimmedValue,
  getStepInfo,
  selectLanguage,
  scrollToTop,
  canShowCloseIcon
} from '@/utils'

const StepTitle = () => import('@/components/StepTitle')
const StepMedia = () => import('@/components/StepMedia')
const SessionContent = () => import('@/components/SessionContent')
const SessionComplete = () => import('@/components/SessionComplete')
const OrderItems = () => import('@/components/OrderItems')
const CustomerInfo = () => import('@/components/CustomerInfo')
const PaymentInfo = () => import('@/components/PaymentInfo')
const PaymentUpdater = () => import('@/components/PaymentUpdater')
const SelectItems = () => import('@/components/SelectItems')
const CustomerMatch = () => import('@/components/CustomerMatch')
const CustomerVerify = () => import('@/components/CustomerVerify')
const InputActionStatus = () => import('@/components/InputActionStatus')
const InputFields = () => import('@/components/InputFields')
const SelectExchangeProduct = () => import('@/components/SelectExchangeProduct')
const ChoiceFields = () => import('@/components/ChoiceFields')
const SelectActionItems = () => import('@/components/SelectActionItems')
const SelectCampaign = () => import('@/components/SelectCampaign')
const Variants = () => import('@/components/Variants')
const Checkout = () => import('@/components/Checkout')
const CheckoutConfirm = () => import('@/components/CheckoutConfirm')
const StepBackButton = () => import('@/components/StepBackButton')
const CustomerPortal = () => import('@/components/CustomerPortal/CustomerPortal')
const OrderTracking = () => import('@/components/Order/OrderTracking')
const InputButton = () => import('@/components/InputButton')
const OrderItemCard = () => import('@/components/OrderItemCard')

export default {
  name: 'Session',
  components: {
    StepMedia,
    StepBackButton,
    SessionContent,
    StepTitle,
    SessionComplete,
    OrderItems,
    CustomerInfo,
    PaymentInfo,
    PaymentUpdater,
    SelectItems,
    CustomerMatch,
    CustomerVerify,
    InputActionStatus,
    InputFields,
    SelectExchangeProduct,
    ChoiceFields,
    SelectActionItems,
    SelectCampaign,
    Variants,
    Checkout,
    CheckoutConfirm,
    CustomerPortal,
    OrderTracking,
    InputButton,
    OrderItemCard
  },
  props: {
    session: Object,
    callCenterPopupContent: {
      type: String,
      default: ''
    }
  },
  data: function() {
    return {
      selectedInput: null,
      selectedIntent: null,
      userSelectedInput: null,
      selectedItem: null,
      updatedItem: null,
      isItemSelection: false,
      inputType: InputType.Choice,
      submitting: false,
      inputData: null,
      enhancedContentInitialized: false,
      statusMessages: [],
      itemSelectionState: InputState.Unknown,
      checkForVariants: false,
      selectedCampaignProduct: null,
      rebill: false,
      subscriptionOnly: false,
      variantSelected: false,
      quantityType: QuantityType.Any
    }
  },
  computed: {
    ...mapState([
      'firstLogin', 'selectedItems', 'selectedItemIndex', 'inputState', 'refreshSessionOnUndo',
      'styleVariables', 'isLoading', 'fulfillments', 'campaign', 'stepStack'
    ]),
    ...mapGetters(['canGotoPreviousItem', 'currentSelectedItem', 'isSessionValid', 'isSessionComplete',
      'isLifelineActive', 'isSessionUserKnown', 'popupCloseCanUndo', 'isMainMenu',
      'popupCloseCanSelectFirstInput', 'popupCloseCanSelectLastInput', 'isAlreadySessionUrl'
    ]),
    canDisplayTitle: function() {
      return !this.isFAQs && !this.isProductInfo
    },
    canShowCloseIcon: function() {
      return canShowCloseIcon(this.session, true)
    },
    showBackButton: function() {
      return this.session && (this.session.can_undo || this.session.is_step_preview)
    },
    isFAQs: function() {
      return (this.inputType === InputType.Faqs || (this.session && this.session.step_type === InputType.Faqs))
    },
    isCustomerInfo: function() {
      return ([InputType.BillingAddress, InputType.ShippingAddress].indexOf(this.inputType) >= 0)
    },
    isPaymentInfo: function() {
      return (this.inputType === InputType.PaymentInfo && !this.rebill)
    },
    isPaymentUpdater: function() {
      return this.inputType === InputType.PaymentInfo && this.rebill
    },
    isProductInfo: function() {
      return (this.inputType === InputType.Product)
    },
    isCheckout: function() {
      return (this.inputType === InputType.Checkout || (this.session && this.session.step_type === InputType.Checkout))
    },
    isCheckoutConfirm: function() {
      return (this.inputType === InputType.CheckoutConfirm)
    },
    isCampaignSelect: function() {
      return (this.inputType === InputType.SelectCampaign)
    },
    isCustomerMatch: function() {
      return (this.inputType === InputType.MatchCustomer)
    },
    currentSelectedItemImageUrl: function() {
      if (this.currentSelectedItem) {
        return getItemImageUrl(this.currentSelectedItem, 'small')
      }

      return getPlaceholderImageUrl('small')
    },
    canInitializeEnhancedContent: function() {
      return this.session && this.session.enhanced_mode
    },
    isIconButtons: function() {
      if (this.session.valid_inputs.length) {
        for (let i = 0; i < this.session.valid_inputs.length; i++) {
          if (!this.session.valid_inputs[i].icon) {
            return false
          }
        }

        return true
      }

      return false
    },
    hasLink: function() {
      if (this.session.valid_inputs.length) {
        for (let i = 0; i < this.session.valid_inputs.length; i++) {
          if (this.isLink(this.session.valid_inputs[i])) {
            return true
          }
        }
      }

      return false
    },
    singleColumnButtons: function() {
      return (this.session.valid_inputs.length < 6) || this.hasLink
    },
    thankYouMessage: function() {
      if (this.session && this.session.thank_you_message && this.session.thank_you_message.length) {
        return this.session.thank_you_message
      }

      return 'You have been logged out.'
    },
    isInputDataEditable: function() {
      let isEditable = false

      if (this.selectedInput && this.selectedInput.input_fields) {
        const fields = Object.values(this.selectedInput.input_fields)

        for (const field of fields) {
          if (field.type !== InputType.Hidden) {
            isEditable = true
            break
          }
        }
      }

      return isEditable
    },
    isBoxButton: function() {
      const mobileIconButtonType = (this.session && this.session.mobile_icon_button_type) ||
        (this.styleVariables && this.styleVariables.mobile_icon_button_type)

      if (mobileIconButtonType) {
        return +mobileIconButtonType === MobileIconButtonType.Box
      }
      return false
    },
    requiresItem: function() {
      return this.session && this.session.valid_inputs && this.session.valid_inputs.some(input => input.requires_item)
    },
    showSelectedProduct: function() {
      const session = this.session

      return session &&
        session.show_selected_product &&
        this.selectedItems.length &&
        this.currentSelectedItem &&
        (!session.is_main_funnel || this.requiresItem)
    },
    isConfirmationPopup: function() {
      return this.session && this.session.step_type === InputType.Confirmation
    },
    canAddToBreadcrumbs: function() {
      return !this.isMainMenu && !this.isFAQs && !this.isCustomerMatch
    },
    isOrderContainerNormal: function() {
      return this.session && this.session.enhanced_mode
    },
    needsInputFields: function() {
      return (this.inputState === InputState.InputFields)
    },
    needsRMAItems: function() {
      if (this.session && this.session.is_background_session) {
        return false
      }
      return (this.inputState === InputState.RMAItems)
    },
    needsRebillItems: function() {
      if (this.session && this.session.is_background_session) {
        return false
      }
      return (this.inputState === InputState.RebillItems)
    },
    needsVariants: function() {
      if (this.session && this.session.is_background_session) {
        return false
      }
      return (this.inputState === InputState.Variants)
    },
    needsCustomerMatch: function() {
      if (this.session && this.session.is_background_session) {
        return false
      }
      return (this.inputState === InputState.NeedsMatch)
    },
    needsCustomerVerification: function() {
      if (this.session && this.session.is_background_session) {
        return false
      }
      return (this.inputState === InputState.NeedsVerification)
    },
    isOrderTracking: function() {
      return this.session && this.session.step_type === InputType.ShipmentTracking
    },
    isRadioGroup: function() {
      return (this.inputType === InputType.Radio)
    },
    isSelect: function() {
      return (this.inputType === InputType.Select)
    },
    isRadioOrSelect: function() {
      return (this.inputType === InputType.RadioSelect)
    },
    isReadyToSubmit: function() {
      return (this.inputState === InputState.OkToSubmit)
    },
    isOkToResubmit: function() {
      return this.isReadyToSubmit && (this.isCustomerMatch || (this.session.valid_inputs.length !== 1))
    },
    isCustomerPortal: function() {
      return this.session && (!this.session.is_popup || this.session.is_background_session) && (
        Object.values(CustomerPortalInputType).indexOf(this.session.step_type) > -1 ||
        Object.values(CustomerPortalInputType).indexOf(this.inputType) > -1
      )
    },
    isUpdateInfoStep: function() {
      return this.session && this.session.step_name &&
        ['update your info', 'update your information'].indexOf(this.session.step_name.toLowerCase()) > -1
    },
    defaultData: function() {
      let data = {}

      if (this.selectedInput.input_fields) {
        const keys = Object.keys(this.selectedInput.input_fields)

        if (keys && keys.length) {
          for (const key of keys) {
            if ('default' in this.selectedInput.input_fields[key]) {
              data[key] = this.selectedInput.input_fields[key].default
            }
          }
        }
      }

      return data
    },
    showFinalSubmitButton: function() {
      return this.session && !this.session.hide_all_buttons
    },
    sessionTitle: function() {
      let title = null
      if (this.session && this.firstLogin && this.session.welcome_message && this.session.welcome_message.length) {
        title = this.session.welcome_message
      } else if (this.session && !this.session.enhanced_mode) {
        title = this.session.label
      }
      return updateFontSizes(title)
    },
    sessionContent: function() {
      if (this.session && this.session.content) {
        return updateFontSizes(this.session.content)
      }
      return null
    },
    sessionSubTitle: function() {
      if (this.session && this.session.subtitle) {
        return updateFontSizes(this.session.subtitle)
      }
      return null
    },
    isSurveyStepCategory: function() {
      return !!this.session && SurveyCategories.includes(this.session.step_category)
    },
    isButtonGroup: function() {
      if (this.session && this.session.valid_inputs && this.session.valid_inputs.length > 0) {
        let input = this.session.valid_inputs[0]
        if (input) {
          return (input.type === InputType.Choice)
        }
      }
      return false
    }
  },
  methods: {
    closePopup: function() {
      if (this.popupCloseCanUndo) {
        this.goBack()
      } else if (this.popupCloseCanSelectFirstInput) {
        this.selectInput(this.session.valid_inputs[0])
      } else if (this.popupCloseCanSelectLastInput) {
        this.selectInput(this.session.valid_inputs[this.session.valid_inputs.length - 1])
      } else if (this.session.valid_inputs.length === 1) {
        if (this.session.can_go_home) {
          this.$store.commit('busy')
          this.$store.dispatch('home').then(() => this.$router.go(0)) // refresh
        } else if (this.session.is_first_step) {
          this.$router.replace({name: 'logout'}) // dont get stuck on a quick link popup - logout
        }
      }
    },
    processUndo: function(count) {
      this.goBack(count)
    },
    sessionInputLabel(input) {
      if (input) {
        return updateFontSizes(input.formatted_label)
      }
      return null
    },
    isLink: function(input) {
      return input.type === InputType.Link ||
        (input.button_styles && input.button_styles.style_type === StepButtonType.TextOnly)
    },
    getCustomClass: function(input, includeButtonClass = false) {
      return getCustomInputClass(this.session, input, includeButtonClass)
    },
    getStyles: function(input) {
      return input && input.button_styles && input.button_styles.style
    },
    selectInput: function(input, data, messages) {
      this.selectedInput = input
      if (data) {
        if (!data.updatePaymentInfo) {
          this.userSelectedInput = input
        }
        delete data['updatePaymentInfo']
      }

      if (typeof data !== 'undefined') {
        this.inputData = data
      }

      if (typeof messages !== 'undefined') {
        this.statusMessages = messages
      }

      console.log({input: input, data: data})

      this.submitForm()
    },
    submitInputAndData: function(input, data, selectedCampaignProduct) {
      this.selectedInput = input
      this.userSelectedInput = input
      this.selectedCampaignProduct = selectedCampaignProduct
      this.submitInputData(data)
    },
    clearInput: function() {
      if ((this.session && this.session.step_type === InputType.EnterTroubleshooter) &&
          (this.selectedInput && this.selectedInput.type === InputType.PathNotFound)) {
        // path not found (i.e. no troubleshooter exists for product) for troubleshooter required
        // data input, going back from the data input should skip back past the (selected) path not found input
        if (this.session.can_undo) {
          this.goBack()
        }
      } else {
        this.selectedInput = null
        this.userSelectedInput = null
        this.selectedIntent = null
        this.updateInputState(false)
      }
    },
    selectItemInput: function(data) {
      this.selectedInput = data.input
      this.onItemsSelected([data.item], data.intent)
    },
    selectItems(items) {
      this.$store.commit('set_selected_items', items)
      this.selectedItem = items[0]
    },
    onItemsSelected: function(items, intent) {
      this.selectItems(items)
      this.selectedIntent = intent
      this.submit()
    },
    selectItemsForRMAOrCancel: function(data, selectedItem) {
      if (selectedItem) {
        this.selectedItem = selectedItem
      }

      this.itemSelectionState = InputState.Unknown
      this.submitInputData(data)
    },
    selectVariants: function(data) {
      if (this.inputData) {
        data = Object.assign({}, this.inputData, data)
      }

      this.checkForVariants = false
      this.selectedCampaignProduct = null
      this.subscriptionOnly = false
      this.quantityType = QuantityType.Any
      this.variantSelected = true
      this.submitInputData(data)
    },
    submitInputData: function(data) {
      this.inputData = data
      let mustSelectVariants = false

      if ((this.selectedCampaignProduct && (this.selectedCampaignProduct.show_variants)) ||
        (this.checkForVariants && this.currentSelectedItem && this.inputData &&
          (this.currentSelectedItem.product.variant_type !== VariantType.None))) {
        if (!this.inputData || !this.inputData['variants'] || !this.inputData['variants'].length) {
          mustSelectVariants = true
        }
      }

      this.checkForVariants = false

      if (this.itemSelectionState === InputState.RMAItems) {
        this.itemSelectionState = InputState.Unknown
        this.$store.commit('set_input_state', InputState.RMAItems)
      } else if (this.itemSelectionState === InputState.RebillItems) {
        this.itemSelectionState = InputState.Unknown
        this.$store.commit('set_input_state', InputState.RebillItems)
      } else if (mustSelectVariants) {
        this.$store.commit('set_input_state', InputState.Variants)
      } else {
        this.$store.commit('set_input_state', InputState.OkToSubmit)
        this.submit()
      }
    },
    submitForm: function() {
      if (this.selectedInput) {
        this.updateInputState(false)
        this.submit()
      }
    },
    editItem: function(input, item) {
      this.selectedInput = input
      this.userSelectedInput = input
      this.selectedItem = item
      this.submitForm()
    },
    onCustomerMatchFailed: function(message) {
      if (this.isCustomerMatch && this.session.additional_inputs && this.session.additional_inputs.length) {
        this.selectInput(this.session.additional_inputs[0]) // customer match step - select no customer found input
      } else {
        this.$store.commit('no_match', message)
      }
    },
    updateInputState: function(resubmit) {
      let inputState = this.selectedInput ? InputState.OkToSubmit : InputState.Unknown

      if (this.session && (
        this.session.requires_customer ||
        (this.selectedInput && this.selectedInput.requires_customer)
      )) {
        if (
          this.session.status === SessionStatus.Unmatched || this.session.prematch_status === PreMatchStatus.Pending
        ) {
          inputState = InputState.NeedsMatch
        } else if (
          (this.session.status === SessionStatus.Unverified) ||
          (this.session.status === SessionStatus.Matched) ||
          (this.session.mfa_status === MFAStatus.Pending)
        ) {
          inputState = InputState.NeedsVerification
        }
      }

      if (this.selectedInput) {
        if (inputState === InputState.OkToSubmit) {
          this.itemSelectionState = InputState.Unknown
          this.checkForVariants = false
          this.selectedCampaignProduct = null

          if (this.selectedInput.input_fields) {
            const keys = Object.keys(this.selectedInput.input_fields)

            if (keys && keys.length) {
              for (const key of keys) {
                const field = this.selectedInput.input_fields[key]
                const fieldType = field.type
                const defaultVal = field.default

                if (key === 'variants') {
                  this.checkForVariants = true

                  // Indexed ReplaceProduct: backend resolved the predetermined replacement product
                  // and flagged that it needs a variant selection. Use it to prompt for that product.
                  if (field.replacement_product) {
                    this.selectedCampaignProduct = {
                      id: field.replacement_product.campaign_product_id,
                      show_variants: field.replacement_product.show_variants,
                      bundle_quantity: field.replacement_product.bundle_quantity,
                      product: {
                        id: field.replacement_product.product_id,
                        variant_type: field.replacement_product.variant_type
                      }
                    }
                  }

                  if (this.session.preview_type === PreviewType.Step && this.session.selected_item) {
                    this.selectItems([this.session.selected_item])
                  }

                  if (field.subscription_only) {
                    this.subscriptionOnly = true
                  }
                } else if (!this.selectedItem) {
                  if ((key === 'items') && (fieldType === InputType.ActiveItem)) {
                    this.itemSelectionState = InputState.RMAItems // select items for RMA
                  } else if ((key === 'items') && (fieldType === InputType.RebillItem)) {
                    this.itemSelectionState = InputState.RebillItems // select items for rebill

                    if (this.selectedInput.type === InputType.PaymentInfo) {
                      if (!this.userSelectedInput) {
                        inputState = InputState.OkToSubmit
                        this.rebill = true
                      } else {
                        inputState = InputState.RebillItems
                      }
                    }
                  } else if ((fieldType !== InputType.Product) && (fieldType !== InputType.Hidden) && !defaultVal) {
                    if (!this.isRadioGroup && !this.isSelect && !this.isRadioOrSelect) {
                      inputState = InputState.InputFields // form input fields
                    }
                  }
                }
              }

              if (inputState === InputState.OkToSubmit) {
                if (this.itemSelectionState === InputState.RMAItems) {
                  inputState = InputState.RMAItems
                } else if (this.checkForVariants) {
                  if (this.selectedCampaignProduct) {
                    // indexed ReplaceProduct whose predetermined replacement product needs a variant
                    inputState = InputState.Variants
                  } else if (this.currentSelectedItem &&
                    (this.currentSelectedItem.product.variant_type !== VariantType.None)) {
                    inputState = InputState.Variants
                  }
                }
              }
            }
          }
        } else {
          this.$store.commit('hide_welcome_message')
        }

        if ([InputState.RMAItems].indexOf(inputState) > -1 && !this.refreshSessionOnUndo) {
          this.$store.commit('refresh_session_on_undo', true)
        }

        this.$store.commit('set_input_state', inputState)
        if (resubmit) {
          if (this.isOkToResubmit) {
            console.log('resubmit')
            this.submit()
          } else {
            this.submitting = true // we selected an input, but not ok to submit yet (i.e. needs customer verification)
            this.$store.commit('ready')
          }
        } else if (this.isCustomerPortal) {
          this.$store.commit('ready')
        }
      } else {
        this.$store.commit('set_input_state', inputState)

        if (resubmit) {
          this.$store.commit('ready')
        }
      }

      if (!this.isLifelineActive && this.session && this.session.is_popup) {
        this.$store.commit('set_display_state', null)
      }
    },
    isItemStatus: function() {
      if (this.session && this.session.is_popup && !this.session.is_background_session) {
        if (this.session.valid_inputs.length > 0) {
          let input = this.session.valid_inputs[0]
          this.selectedInput = input
          return input.type === InputType.ItemStatus
        }
      }
      return (this.inputType === InputType.ItemStatus)
    },
    isInputActionStatus: function() {
      if (this.session && this.session.is_popup && !this.session.is_background_session) {
        if (this.session.valid_inputs.length > 0) {
          let input = this.session.valid_inputs[0]
          if (input.type === InputType.InputActionStatus) {
            this.selectedInput = input
            this.$store.commit('set_input_state', InputState.OkToSubmit)
            return true
          }
          return false
        }
      }
      return (this.inputType === InputType.InputActionStatus)
    },
    getShipmentReturnLabel: function(qrCode) {
      if (this.currentSelectedItem) {
        this.$store.commit('busy')
        let newWindow = window.open('', '_blank')
        this.$store.dispatch('load_shipment_return_label', {
          item: this.currentSelectedItem, qr_code: qrCode, newWindow})
      }
    },
    closeCallCenterPopup: function() {
      this.$bvModal.hide('call-center-popup')
    },
    onCloseCallCenterPopup: function() {
      this.init()

      if (this.isInputActionStatus()) {
        this.submit()
      } else {
        this.$emit('close')
      }
    },
    showLoader: function() {
      if (!this.isCustomerPortal || this.session.is_popup) {
        if (this.selectedInput) {
          this.$root.$emit('toggle-processing-gifs', null, this.selectedInput.action_results)
        }
        this.$store.commit('busy')
      } else {
        this.$store.commit('showCPLoader')
      }
    },
    hideLoader: function() {
      if (this.selectedInput && this.selectedInput.processing_gif) {
        this.$root.$emit('toggle-processing-gifs', null)
      }
      this.$store.commit('ready')
      this.$store.commit('hideCPLoader')
    },
    submit: function() {
      const isLastStepPreviewStep = this.session.is_step_preview && this.selectedInput && !this.selectedInput.next_step
      if (!this.isReadyToSubmit || isLastStepPreviewStep) {
        this.submitting = true // we selected an input, but not ok to submit yet (i.e. needs customer verification)
        this.hideLoader()
      } else if (this.selectedInput.type === InputType.ShipmentReturnLabel) {
        this.getShipmentReturnLabel(false)
      } else if (this.selectedInput.type === InputType.ShipmentReturnQRCode) {
        this.getShipmentReturnLabel(true)
      } else {
        let params = {
          input: this.selectedInput.id
        }

        if (this.selectedItem) {
          params['item'] = this.selectedItem.id
        }

        if (
          this.selectedIntent &&
          isItemSelectionInput(this.selectedInput) &&
          this.session &&
          this.session.use_intents
        ) {
          params['intent'] = this.selectedIntent
        }

        if (this.inputData) {
          params['data'] = Object.assign(this.defaultData, this.inputData)
        }

        this.showLoader()
        this.$store.dispatch('input', params)
          .then(session => {
            this.$store.commit('update_selected_item')
            console.log(session.step)

            // if we were passed some status messages to display, commit them so that they show up
            if (this.statusMessages && this.statusMessages.length) {
              this.$store.commit('messages', this.statusMessages)
            }
            if (this.selectedInput && this.selectedInput.type === InputType.Checkout) {
              this.$store.commit('set_cart', [])
            }

            // Update the step stack based on whether the step can be undone
            if (!session.can_undo) {
              this.$store.commit('clear_step_stack')
            }

            if (this.canAddToBreadcrumbs && this.isSessionValid) {
              this.$store.commit('push_to_step_stack', getStepInfo(session))
            }
            if (!this.refreshSessionOnUndo) {
              // Skip the browser-back "undo" breadcrumb push while inside a lifeline. The lifeline
              // runs in a modal (its own close/exit handling), so this history trick is not needed
              // there - and because `can_go_home` is true during a lifeline, letting Undo.vue mount
              // via this push would immediately dispatch an `undo`, kicking the user back to the
              // previous lifeline step (e.g. the "do you need help?" prompt).
              if (!session.is_lifeline_active) {
                this.$router.push({ name: 'undo', params: { step: getTrimmedValue(session.step_slug) } }, () => {
                  this.$router.push({ name: 'session', params: { step: getTrimmedValue(session.step_slug) } })

                  if (this.isFAQs) {
                    this.$router.replace({name: 'faq-topics'})
                  }
                }, (error) => console.log('ERROR', error))
              }
              if (!isFirstInputDone()) {
                setFirstInputDone()
              }
            } else if (this.refreshSessionOnUndo) {
              this.$store.commit('refresh_session_on_undo', false)
              setFirstInputDone()
            }

            this.refresh()
            scrollToTop() // scroll to top on each input
          })
          .catch(err => {
            console.log(err)
            this.updateInputState(false)

            if (err.response && err.response.status === 400) {
              // handle input field validation errors
              const error = getErrorData(err)
              console.log(error)

              if (error.data && error.data.refresh_data) {
                this.$store.commit('set_warning',
                  'Sorry, your session has expired.  Please refresh the page and try again.')
              } else if (this.needsInputFields) {
                console.log('needs input fields')
                this.$store.commit('error', error.errors)
              } else if (this.selectedInput && this.selectedInput.type === InputType.Checkout) {
                this.$store.commit('checkout_error', error.errors)
              } else {
                this.$store.commit('critical_error', err)
                this.refresh()
              }
            } else if (err.response && err.response.status === 402) { // payment required
              // popup payment form - input then needs to be resubmitted with input data {card: payment info}
              this.$store.commit('error', err) // remove this error and show a friendly message along with the payment form
            } else if (err.response && err.response.status === 422) { // non-critical error (more like a warning)
              this.$store.commit('error', err)
            } else {
              this.$store.commit('critical_error', err)
              this.refresh()
            }
          })
      }
    },
    refresh: function() {
      this.$root.$emit('refresh-session')
    },
    goBack: function(count = 0) {
      console.log('go back', count)

      if (this.session && this.session.is_step_preview && this.session.is_first_step) {
        return // ignore back button if this is a preview step
      }

      let canUndo = !this.submitting && this.session && this.session.can_undo

      if (canUndo) {
        if (count === 0) {
          history.back()
        }
        this.$store.commit('busy')
        this.$store.dispatch('undo', count ? { count: count } : {})
          .then((session) => {
            if (count > 0) {
              const slug = getTrimmedValue(session.step_slug)
              // 1️⃣ Replace current URL to clear forward entries
              this.$router.replace({ name: 'session', params: { step: slug } })
              this.$router.push({ name: 'session', params: { step: slug } })
              this.$router.replace({ name: 'session', params: { step: slug } })
            }
            this.$store.commit('remove_steps_from_step_stack', count)
            this.$store.commit('clear_messages')
            this.$store.commit('increment_undo_count')
            this.refresh()
          })
          .catch(err => {
            console.log(err)
            if (!err.response || err.response.status !== 422) {
              this.$store.commit('critical_error', err)
            }
          })
      } else {
        this.$store.commit('increment_undo_count')
        this.$store.commit('clear_messages')
        // Clear step stack if we can't undo
        if (!this.submitting) {
          this.$store.commit('clear_step_stack')
        }
        this.refresh()
      }
    },
    init: function() {
      console.log('session init')
      this.showLoader()
      this.$store.commit('set_input_state', InputState.Unknown)
      this.inputType = InputType.Choice
      this.selectedInput = null
      this.selectedIntent = null
      this.userSelectedInput = null
      this.updatedItem = null
      this.isItemSelection = false
      this.submitting = false
      this.inputData = null
      this.subscriptionOnly = false
      this.quantityType = QuantityType.Any
      this.statusMessages = []
      this.itemSelectionState = InputState.Unknown
      this.checkForVariants = false
      this.selectedCampaignProduct = null
      const autoSelectItem = this.session && (
        this.session.source === SessionSource.API ||
        this.session.step_type === InputType.ItemStatus)
      const _this = this

      if (!this.session || this.session.step_type !== InputType.Confirmation) {
        this.selectedItem = null
      }

      if (this.session) {
        if (this.session.is_first_step) {
          if ((this.session.is_step_preview || autoSelectItem) && this.session.selected_item) {
            this.$store.commit('set_selected_items', [this.session.selected_item])
          } else {
            this.$store.commit('clear_selected_items')
          }
        } else if (autoSelectItem && this.session.selected_item) {
          this.$store.commit('set_selected_items', [this.session.selected_item])
        }

        if (this.session.valid_inputs.length === 1) {
          // if there's only 1 valid input, select it by default
          const autoSelectedInput = this.session.valid_inputs[0]
          this.inputType = autoSelectedInput.type

          if (this.session.step_type !== InputType.Confirmation) {
            this.selectedInput = autoSelectedInput
            if (isItemSelectionInput(this.selectedInput)) {
              this.isItemSelection = true
            }
          }
        } else if (this.session.valid_inputs.length > 1) {
          // multiple inputs - get the type
          this.inputType = this.session.valid_inputs[0].type
        }

        if (this.isOrderTracking && this.session.selected_item) {
          this.$store.commit('busy')
          this.$store.dispatch('load_fulfillments', this.session.selected_item)
            .then(_ => {
              this.$store.commit('ready')
              setTimeout(() => this.$bvModal.show('fulfillment-tracking-modal'))
            })
            .catch(() => {
              this.$store.commit('ready')
            })
        }

        // show/hide the lifeline popup or contact form
        if (this.session.show_lifeline_contact) {
          this.$bvModal.hide('lifeline')
          setTimeout(() => this.$bvModal.show('contact'))
        } else if (this.session.is_lifeline_active) {
          this.$bvModal.hide('contact')
          setTimeout(() => this.$bvModal.show('lifeline'))
        }

        setTimeout(() => {
          _this.initializeEnhanceContent()
        }, 50)
      }

      this.updateInputState(false)
      this.hideLoader()
      this.$emit('ready')
    },
    setAltImg: function(event) {
      event.target.src = getPlaceholderImageUrl('large')
    },
    isSelectedProductRequired: function() {
      if (this.session && !this.variantSelected && this.session.valid_inputs && this.session.valid_inputs.length) {
        for (const input of this.session.valid_inputs) {
          const keys = Object.keys(input.input_fields)

          if (keys && keys.length) {
            const key = keys[0]
            const field = input.input_fields[key]

            if (field.type === InputType.Product) {
              this.subscriptionOnly = !!field.subscription_only
              this.quantityType = field.quantity_type || QuantityType.Any
              return true
            }
          }
        }
      }
      this.variantSelected = false
      return false
    },
    initializeEnhanceContent: function() {
      const _this = this

      if (!this.canInitializeEnhancedContent || this.enhancedContentInitialized) {
        return // nothing to do
      }

      // Function to perform the actual initialization
      const performInitialization = () => {
        const pageContents = document.querySelectorAll('.page-content')

        // If no content found, return false to indicate initialization didn't happen
        if (pageContents.length === 0) {
          return false
        }

        pageContents.forEach(pageContent => {
          if (pageContent) {
            this.enhancedContentInitialized = true
            // skip in case of step preview
            if (_this.session && !_this.session.is_step_preview) {
              // setup path inputs
              const pathInputs = Array.from(pageContent.querySelectorAll("[ctype='path-input']")) || []
              pathInputs.forEach(element => {
                const elemType = element.getAttribute('elemtype')
                if (elemType === 'button' || elemType === 'link') {
                  const validInputs = _this.session.valid_inputs
                  const hasInput = validInputs.some((validInput) => validInput.input_id === +element.getAttribute('input'))
                  if (hasInput) {
                    if (elemType === 'link') {
                      element.removeAttribute('target')
                      element.removeAttribute('href')
                    }
                    if (!element.callback) {
                      element.addEventListener('click', element.callback = (e) => {
                        const inputEl = e.target.closest('a[elemtype=button]') || e.target.closest('a[elemtype=link]')
                        const input = validInputs.find((validInput) => validInput.input_id === +inputEl.getAttribute('input'))
                        _this.selectInput(input)
                        return false
                      })
                    }
                  } else {
                    element.remove()
                  }
                }
              })
            }

            // setup back buttons
            const backBtns = Array.from(pageContent.querySelectorAll("[elemtype='back-button']")) || []
            backBtns.forEach(element => {
              element.addEventListener('click', (e) => {
                this.goBack()
                return false
              })
            })
          }
        })

        pageContents.forEach(pageContent => {
          if (pageContent.style) {
            pageContent.style.setProperty('display', 'block')
          }
        })

        return true
      }

      // Try immediate initialization
      if (performInitialization()) {
        return
      }

      // If content not available, use MutationObserver to wait for it
      let attempts = 0
      const maxAttempts = 50 // Maximum 5 seconds (50 * 100ms)

      const waitForContent = () => {
        if (performInitialization()) {
          return // Success, content initialized
        }

        attempts++
        if (attempts < maxAttempts) {
          // Use requestAnimationFrame for better performance
          window.requestAnimationFrame(() => {
            setTimeout(waitForContent, 100)
          })
        }
      }

      // Start waiting for content
      this.$nextTick(() => {
        waitForContent()
      })
    }
  },
  created() {
    if (this.$route.query['language']) {
      selectLanguage(this.$route.query['language'].toString().toLowerCase())
    }
    this.init()
    if (this.isFAQs) {
      this.$router.replace({name: 'faq-topics'})
    }
    if (this.isProductInfo) {
      this.$router.replace({name: 'products'})
    }
    if (this.isMainMenu && !this.isAlreadySessionUrl) {
      this.$router.replace({name: 'session'})
    }
  },
  updated() {
    if (this.isFAQs) {
      setTimeout(() => {
        if (window.location.pathname !== '/topics') {
          this.$router.replace({name: 'faq-topics'})
        }
      }, 100)
    }
    if (this.isProductInfo) {
      this.$router.replace({
        name: 'products',
        params: {
          input: this.selectedInput,
          title: this.session.label,
          subtitle: this.session ? this.session.subtitle : '',
          submit: this.selectInput,
          back: this.goBack
        }
      })
    }

    if (this.isMainMenu && !this.isAlreadySessionUrl) {
      this.$router.replace({name: 'session'})
    }

    this.initializeEnhanceContent()
    recalculateFontSizes()
  },
  mounted() {
    this.initializeEnhanceContent()
  }
}
</script>

<style scoped>
.selected-product-wrapper {
  width: 100%;
  display: flex;
  justify-content: center;
}

.product-wrapper {
  margin-bottom: 20px;
  padding: 0 8px;
}

.product-wrapper-enhanced {
  margin-top: 20px;
}

.selected-product-wrapper .orders-container {
  width: 100%;
  margin: 0 auto;
  box-shadow: none !important;
  border: 1px solid #DFE0E0 !important;
  font-size: 12px !important;
  max-width: 768px;
}

.selected-product-wrapper .order-container {
  background: #FFF;
  margin-bottom: 0 !important;
}

.orders-container-normal {
  width: 100% !important;
}

/* Mobile - 100% width */
@media (max-width: 767px) {
  .selected-product-wrapper .orders-container {
    width: 100%;
  }

  .selected-product-col {
    padding: 0;
  }
}
</style>
